import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Save, Upload } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/perfil")({
  head: () => ({ meta: [{ title: "Perfil — Admin" }] }),
  component: AgentProfileAdmin,
});

type FormState = {
  id: string | null;
  full_name: string;
  bio: string;
  years_experience: string;
  zones: string;
  phone_whatsapp: string;
  email: string;
  instagram_url: string;
  facebook_url: string;
  photo_url: string;
};

const empty: FormState = {
  id: null,
  full_name: "",
  bio: "",
  years_experience: "",
  zones: "",
  phone_whatsapp: "",
  email: "",
  instagram_url: "",
  facebook_url: "",
  photo_url: "",
};

function AgentProfileAdmin() {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "agent-profile"],
    queryFn: async () => {
      const { data } = await supabase
        .from("agent_profile")
        .select("*")
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
  });

  useEffect(() => {
    if (data) {
      setForm({
        id: data.id,
        full_name: data.full_name ?? "",
        bio: data.bio ?? "",
        years_experience: data.years_experience?.toString() ?? "",
        zones: (data.zones ?? []).join(", "),
        phone_whatsapp: data.phone_whatsapp ?? "",
        email: data.email ?? "",
        instagram_url: data.instagram_url ?? "",
        facebook_url: data.facebook_url ?? "",
        photo_url: data.photo_url ?? "",
      });
    }
  }, [data]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const uploadPhoto = async (file: File) => {
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() ?? "jpg";
      const path = `agent/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage
        .from("property-images")
        .upload(path, file, { upsert: false, contentType: file.type });
      if (error) throw error;
      const { data: pub } = supabase.storage.from("property-images").getPublicUrl(path);
      // Try signed URL as fallback if bucket private
      let url = pub.publicUrl;
      const { data: signed } = await supabase.storage
        .from("property-images")
        .createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
      if (signed?.signedUrl) url = signed.signedUrl;
      set("photo_url", url);
      toast.success("Foto cargada");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo subir la foto");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        bio: form.bio.trim() || null,
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        zones: form.zones
          .split(",")
          .map((z) => z.trim())
          .filter(Boolean),
        phone_whatsapp: form.phone_whatsapp.trim() || null,
        email: form.email.trim() || null,
        instagram_url: form.instagram_url.trim() || null,
        facebook_url: form.facebook_url.trim() || null,
        photo_url: form.photo_url.trim() || null,
      };
      if (form.id) {
        const { error } = await supabase
          .from("agent_profile")
          .update(payload)
          .eq("id", form.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("agent_profile").insert(payload);
        if (error) throw error;
      }
      toast.success("Perfil actualizado");
      qc.invalidateQueries({ queryKey: ["admin", "agent-profile"] });
      qc.invalidateQueries({ queryKey: ["agent-profile"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return <div className="h-40 animate-pulse rounded-xl bg-secondary/60" />;
  }

  return (
    <form onSubmit={save} className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Perfil del agente</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Estos datos se muestran en la página pública <code>/agente</code>.
        </p>
      </div>

      <section className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-5">
          <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-secondary">
            {form.photo_url ? (
              <img src={form.photo_url} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="grid h-full w-full place-items-center text-3xl text-muted-foreground/50">
                D
              </div>
            )}
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-secondary">
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Cambiar foto
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadPhoto(f);
              }}
            />
          </label>
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Nombre completo *">
          <input
            className={inputCls}
            value={form.full_name}
            onChange={(e) => set("full_name", e.target.value)}
            required
          />
        </Field>
        <Field label="Años de experiencia">
          <input
            className={inputCls}
            type="number"
            min="0"
            value={form.years_experience}
            onChange={(e) => set("years_experience", e.target.value)}
          />
        </Field>
        <Field label="WhatsApp / Teléfono">
          <input
            className={inputCls}
            value={form.phone_whatsapp}
            onChange={(e) => set("phone_whatsapp", e.target.value)}
            placeholder="+595 981 000 000"
          />
        </Field>
        <Field label="Email">
          <input
            className={inputCls}
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
          />
        </Field>
        <Field label="Instagram (URL)">
          <input
            className={inputCls}
            value={form.instagram_url}
            onChange={(e) => set("instagram_url", e.target.value)}
            placeholder="https://instagram.com/..."
          />
        </Field>
        <Field label="Facebook (URL)">
          <input
            className={inputCls}
            value={form.facebook_url}
            onChange={(e) => set("facebook_url", e.target.value)}
            placeholder="https://facebook.com/..."
          />
        </Field>
      </div>

      <Field label="Zonas (separadas por coma)">
        <input
          className={inputCls}
          value={form.zones}
          onChange={(e) => set("zones", e.target.value)}
          placeholder="Villa Morra, Recoleta, Las Lomas"
        />
      </Field>

      <Field label="Biografía">
        <textarea
          className={`${inputCls} resize-none`}
          rows={5}
          value={form.bio}
          onChange={(e) => set("bio", e.target.value)}
        />
      </Field>

      <button
        type="submit"
        disabled={saving}
        className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-soft transition hover:brightness-110 disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Guardar cambios
      </button>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
